import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  RefreshCw,
  Search,
  Phone,
  Mail,
  Euro,
  LogOut,
  Bell,
  Car,
  Calendar,
  Send,
  MessageSquare,
  ChevronRight,
  BarChart3,
  Users,
  Flame,
  AlertCircle,
  CheckCircle,
  Package,
  Ship,
  XCircle,
  GripVertical,
  Settings,
  Key,
  User,
  Eye,
  EyeOff,
  PieChart as PieChartIcon,
} from "lucide-react";
import axios from "axios";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import {
  DndContext,
  DragOverlay,
  closestCorners,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  useDroppable,
} from "@dnd-kit/core";
import {
  useSortable,
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

// Droppable Column Component
const DroppableColumn = ({ id, children }) => {
  const { setNodeRef, isOver } = useDroppable({ id });
  
  return (
    <div
      ref={setNodeRef}
      className={`p-2 space-y-2 max-h-[calc(100vh-280px)] overflow-y-auto min-h-[100px] transition-colors ${
        isOver ? "bg-neon-lime/5" : ""
      }`}
    >
      {children}
    </div>
  );
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LEAD_COLORS = {
  hot: "#22c55e",
  warm: "#f59e0b",
  cold: "#3b82f6",
  lost: "#ef4444",
};

const PIPELINE_COLUMNS = [
  { key: "new", label: "NOUVEAU", color: "#3b82f6", icon: AlertCircle },
  { key: "contacted", label: "CONTACTÉ", color: "#8b5cf6", icon: Phone },
  { key: "contract_signed", label: "CONTRAT VALIDÉ", color: "#22c55e", icon: CheckCircle },
  { key: "searching", label: "RECHERCHE", color: "#f59e0b", icon: Search },
  { key: "importing", label: "IMPORT", color: "#06b6d4", icon: Ship },
  { key: "delivered", label: "LIVRÉ", color: "#10b981", icon: Package },
  { key: "abandoned", label: "ABANDONNÉ", color: "#ef4444", icon: XCircle },
];

const EMAIL_TEMPLATES = [
  { value: "followup", label: "📧 Relance" },
  { value: "reminder", label: "🔔 Rappel" },
  { value: "contract_sent", label: "📄 Contrat envoyé" },
  { value: "vehicle_found", label: "🎯 Véhicule trouvé" },
  { value: "shipping_update", label: "🚢 Mise à jour expédition" },
  { value: "delivery_scheduled", label: "🎉 Livraison programmée" },
  { value: "custom", label: "✏️ Personnalisé" },
];

// Sortable Lead Card Component
const SortableLeadCard = ({ lead, openLeadDetail, formatDate }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: lead.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      data-testid={`lead-card-${lead.id}`}
      className="bg-zinc-900 border border-zinc-800 hover:border-zinc-600 p-3 cursor-pointer transition-all group"
    >
      {/* Drag handle */}
      <div className="flex items-start gap-2">
        <div
          {...attributes}
          {...listeners}
          className="cursor-grab active:cursor-grabbing p-1 -ml-1 text-zinc-600 hover:text-zinc-400 touch-none"
        >
          <GripVertical className="w-4 h-4" />
        </div>
        <div className="flex-1" onClick={() => openLeadDetail(lead)}>
          {/* Vehicle / Pack */}
          <div className="flex items-center gap-2 mb-2">
            <Car className="w-4 h-4 text-neon-lime flex-shrink-0" />
            <span className="text-white text-sm font-bold truncate">
              {lead.vehicle_target || lead.vehicle_type || lead.pack_interest || "Non spécifié"}
            </span>
          </div>
          
          {/* Contact Name */}
          <p className="text-zinc-400 text-sm truncate mb-1">
            {lead.name}
          </p>
          
          {/* Phone */}
          <p className="text-zinc-500 text-xs font-mono mb-2">
            {lead.phone}
          </p>
          
          {/* Score & Date */}
          <div className="flex items-center justify-between text-xs">
            <span
              className="px-2 py-0.5 font-bold"
              style={{
                backgroundColor: LEAD_COLORS[lead.lead_status] || LEAD_COLORS.cold,
                color: lead.lead_status === "warm" ? "#000" : "#fff",
              }}
            >
              Score: {lead.lead_score}
            </span>
            <span className="text-zinc-600">
              {formatDate(lead.created_at)}
            </span>
          </div>
          
          {/* Project type badge */}
          <div className="mt-2 flex items-center gap-1 flex-wrap">
            {lead.project_type === "europe" && (
              <span className="text-xs px-1.5 py-0.5 bg-blue-900/50 text-blue-400">🇪🇺 Europe</span>
            )}
            {lead.project_type === "korea" && (
              <span className="text-xs px-1.5 py-0.5 bg-purple-900/50 text-purple-400">🇰🇷 Corée</span>
            )}
            {lead.source === "payment" && (
              <span className="text-xs px-1.5 py-0.5 bg-green-900/50 text-green-400">💳 Payé</span>
            )}
            {!lead.is_processed && (
              <span className="text-xs px-1.5 py-0.5 bg-neon-lime/20 text-neon-lime animate-pulse">NEW</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Lead Card for Drag Overlay
const LeadCardOverlay = ({ lead, formatDate }) => {
  return (
    <div className="bg-zinc-900 border-2 border-neon-lime p-3 shadow-lg shadow-neon-lime/20 w-72">
      <div className="flex items-center gap-2 mb-2">
        <Car className="w-4 h-4 text-neon-lime flex-shrink-0" />
        <span className="text-white text-sm font-bold truncate">
          {lead.vehicle_target || lead.vehicle_type || lead.pack_interest || "Non spécifié"}
        </span>
      </div>
      <p className="text-zinc-400 text-sm truncate mb-1">{lead.name}</p>
      <p className="text-zinc-500 text-xs font-mono">{lead.phone}</p>
    </div>
  );
};

const AdminPage = () => {
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [showCharts, setShowCharts] = useState(false);
  
  // Drag and drop state
  const [activeId, setActiveId] = useState(null);
  const [activeLead, setActiveLead] = useState(null);
  
  // Lead detail dialog
  const [selectedLead, setSelectedLead] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [newNote, setNewNote] = useState("");
  
  // Email dialog
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState("");
  const [customSubject, setCustomSubject] = useState("");
  const [customMessage, setCustomMessage] = useState("");
  const [sendingEmail, setSendingEmail] = useState(false);

  // Settings dialog
  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newUsername, setNewUsername] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  const [currentAdminUsername, setCurrentAdminUsername] = useState("");

  // DnD Sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor)
  );

  const fetchData = async () => {
    setLoading(true);
    const token = localStorage.getItem("adminToken");
    try {
      const [leadsRes, statsRes] = await Promise.all([
        axios.get(`${API}/leads?token=${token}`),
        axios.get(`${API}/leads/stats?token=${token}`),
      ]);
      setLeads(leadsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
      if (error.response?.status === 401) {
        localStorage.removeItem("adminToken");
        navigate("/espace-pro-fastandcar");
      }
      toast.error("Erreur de chargement");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Set page title and noindex
    document.title = "Gestion des Leads - Espace Pro | Fast&Car";
    
    let metaRobots = document.querySelector('meta[name="robots"]');
    if (!metaRobots) {
      metaRobots = document.createElement('meta');
      metaRobots.name = 'robots';
      document.head.appendChild(metaRobots);
    }
    metaRobots.content = 'noindex, nofollow';
    
    fetchData();
    
    return () => {
      if (metaRobots) {
        metaRobots.content = 'index, follow';
      }
    };
  }, []);

  const handleLogout = () => {
    localStorage.removeItem("adminToken");
    navigate("/espace-pro-fastandcar");
    toast.success("Déconnexion réussie");
  };

  const openSettingsDialog = async () => {
    const token = localStorage.getItem("adminToken");
    try {
      const response = await axios.get(`${API}/admin/current?token=${token}`);
      setCurrentAdminUsername(response.data.username);
      setNewUsername(response.data.username);
    } catch (error) {
      console.error("Error fetching admin info:", error);
    }
    setSettingsDialogOpen(true);
  };

  const handleSaveSettings = async () => {
    // Validation
    if (!currentPassword) {
      toast.error("Veuillez entrer votre mot de passe actuel");
      return;
    }
    
    if (newPassword && newPassword !== confirmPassword) {
      toast.error("Les nouveaux mots de passe ne correspondent pas");
      return;
    }
    
    if (newPassword && newPassword.length < 6) {
      toast.error("Le nouveau mot de passe doit contenir au moins 6 caractères");
      return;
    }

    setSavingSettings(true);
    const token = localStorage.getItem("adminToken");
    
    try {
      const response = await axios.post(`${API}/admin/change-credentials?token=${token}`, {
        current_password: currentPassword,
        new_username: newUsername !== currentAdminUsername ? newUsername : null,
        new_password: newPassword || null,
      });
      
      if (response.data.success) {
        toast.success("Identifiants mis à jour avec succès !");
        setSettingsDialogOpen(false);
        setCurrentPassword("");
        setNewPassword("");
        setConfirmPassword("");
        if (response.data.username) {
          setCurrentAdminUsername(response.data.username);
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la mise à jour");
    } finally {
      setSavingSettings(false);
    }
  };

  const triggerReminders = async () => {
    try {
      await axios.post(`${API}/admin/trigger-reminders`);
      toast.success("Rappels automatiques envoyés !");
    } catch (error) {
      toast.error("Erreur lors de l'envoi des rappels");
    }
  };

  const updatePipelineStatus = async (leadId, newStatus) => {
    const token = localStorage.getItem("adminToken");
    try {
      await axios.patch(`${API}/leads/${leadId}?token=${token}`, { pipeline_status: newStatus });
      toast.success("Statut mis à jour");
      fetchData();
    } catch (error) {
      toast.error("Erreur de mise à jour");
    }
  };

  const updateLeadStatus = async (leadId, newStatus) => {
    const token = localStorage.getItem("adminToken");
    try {
      await axios.patch(`${API}/leads/${leadId}?token=${token}`, { lead_status: newStatus });
      toast.success("Température mise à jour");
      fetchData();
    } catch (error) {
      toast.error("Erreur de mise à jour");
    }
  };

  const addNote = async (leadId) => {
    if (!newNote.trim()) return;
    const token = localStorage.getItem("adminToken");
    try {
      await axios.post(`${API}/leads/${leadId}/notes?note=${encodeURIComponent(newNote)}&token=${token}`);
      toast.success("Note ajoutée");
      setNewNote("");
      fetchData();
      // Refresh selected lead
      const updatedLead = await axios.get(`${API}/leads/${leadId}?token=${token}`);
      setSelectedLead(updatedLead.data);
    } catch (error) {
      toast.error("Erreur lors de l'ajout");
    }
  };

  const sendEmail = async () => {
    if (!selectedLead || !selectedTemplate) return;
    
    if (selectedTemplate === "custom" && (!customSubject || !customMessage)) {
      toast.error("Veuillez remplir le sujet et le message");
      return;
    }

    setSendingEmail(true);
    const token = localStorage.getItem("adminToken");
    try {
      await axios.post(`${API}/leads/${selectedLead.id}/send-email?token=${token}`, {
        lead_id: selectedLead.id,
        template_type: selectedTemplate,
        custom_subject: selectedTemplate === "custom" ? customSubject : null,
        custom_message: selectedTemplate === "custom" ? customMessage : null,
      });
      toast.success(`Email envoyé à ${selectedLead.email}`);
      setEmailDialogOpen(false);
      setSelectedTemplate("");
      setCustomSubject("");
      setCustomMessage("");
      fetchData();
    } catch (error) {
      toast.error("Erreur lors de l'envoi");
    } finally {
      setSendingEmail(false);
    }
  };

  const openLeadDetail = (lead) => {
    setSelectedLead(lead);
    setDetailDialogOpen(true);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const date = new Date(dateStr);
    return date.toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return "-";
    const date = new Date(dateStr);
    return date.toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const filteredLeads = leads.filter((lead) => {
    const matchesSearch =
      searchTerm === "" ||
      lead.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lead.phone.includes(searchTerm) ||
      lead.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (lead.vehicle_type && lead.vehicle_type.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (lead.vehicle_target && lead.vehicle_target.toLowerCase().includes(searchTerm.toLowerCase()));
    return matchesSearch;
  });

  const getLeadsByStatus = (status) => {
    return filteredLeads.filter((lead) => (lead.pipeline_status || "new") === status);
  };

  // Drag and Drop handlers
  const handleDragStart = (event) => {
    const { active } = event;
    setActiveId(active.id);
    const lead = leads.find((l) => l.id === active.id);
    setActiveLead(lead);
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    setActiveId(null);
    setActiveLead(null);

    if (!over) return;

    const leadId = active.id;
    const overId = over.id;
    
    // Find the target column - check if it's a column key or if we need to find the parent
    let targetColumnKey = null;
    
    // Check if dropped directly on a column
    const directColumn = PIPELINE_COLUMNS.find((col) => col.key === overId);
    if (directColumn) {
      targetColumnKey = directColumn.key;
    } else {
      // Dropped on another lead - find which column that lead is in
      const overLead = leads.find((l) => l.id === overId);
      if (overLead) {
        targetColumnKey = overLead.pipeline_status || "new";
      }
    }
    
    if (targetColumnKey) {
      const currentLead = leads.find((l) => l.id === leadId);
      
      if (currentLead && (currentLead.pipeline_status || "new") !== targetColumnKey) {
        // Optimistically update the UI
        setLeads((prev) =>
          prev.map((l) =>
            l.id === leadId ? { ...l, pipeline_status: targetColumnKey } : l
          )
        );

        // Get the column label for toast
        const targetColumn = PIPELINE_COLUMNS.find((col) => col.key === targetColumnKey);

        // Update on the server
        const token = localStorage.getItem("adminToken");
        try {
          await axios.patch(`${API}/leads/${leadId}?token=${token}`, {
            pipeline_status: targetColumnKey,
          });
          toast.success(`Lead déplacé vers ${targetColumn?.label || targetColumnKey}`);
        } catch (error) {
          // Revert on error
          fetchData();
          toast.error("Erreur lors du déplacement");
        }
      }
    }
  };

  const handleDragOver = (event) => {
    // Visual feedback handled by DnD kit
  };

  return (
    <div data-testid="admin-page" className="pt-32 md:pt-40 min-h-screen bg-zinc-950">
      {/* Header */}
      <section className="py-3 md:py-4 bg-black border-b border-zinc-800 sticky top-16 md:top-24 z-30">
        <div className="max-w-full mx-auto px-3 md:px-4">
          <div className="flex items-center justify-between gap-2 md:gap-4 flex-wrap">
            <div className="flex items-center gap-2 md:gap-4">
              <h1 className="font-unbounded text-base md:text-xl font-bold text-white">
                CRM <span className="text-neon-lime">Fast&Car</span>
              </h1>
              {stats && (
                <span className="text-zinc-500 text-xs hidden sm:inline">
                  {stats.total} leads • {stats.unprocessed} à traiter
                </span>
              )}
            </div>
            
            <div className="flex items-center gap-2 md:gap-3">
              <div className="relative hidden sm:block">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
                <Input
                  placeholder="Rechercher..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 bg-zinc-900 border-zinc-800 text-white w-32 md:w-48 h-8 md:h-9 rounded-none text-sm"
                  data-testid="search-input"
                />
              </div>
              
              <Button
                onClick={() => setShowCharts(!showCharts)}
                variant="outline"
                size="sm"
                className="border-zinc-700 text-zinc-400 h-8 w-8 md:h-9 md:w-auto p-0 md:px-3"
              >
                <BarChart3 className="w-4 h-4" />
              </Button>
              
              <Button
                onClick={triggerReminders}
                variant="outline"
                size="sm"
                className="border-zinc-700 text-zinc-400 h-8 w-8 md:h-9 md:w-auto p-0 md:px-3 hidden sm:flex"
              >
                <Bell className="w-4 h-4" />
              </Button>
              
              <Button
                onClick={fetchData}
                variant="outline"
                size="sm"
                className="border-zinc-700 text-zinc-400 h-8 w-8 md:h-9 md:w-auto p-0 md:px-3"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
              </Button>
              
              <Button
                onClick={openSettingsDialog}
                variant="outline"
                size="sm"
                className="border-zinc-700 text-zinc-400 h-8 w-8 md:h-9 md:w-auto p-0 md:px-3"
                data-testid="settings-btn"
              >
                <Settings className="w-4 h-4" />
              </Button>
              
              <Button
                onClick={() => navigate("/espace-pro-fastandcar/analytics")}
                variant="outline"
                size="sm"
                className="border-zinc-700 text-zinc-400 h-8 w-8 md:h-9 md:w-auto p-0 md:px-3"
                data-testid="analytics-btn"
              >
                <PieChartIcon className="w-4 h-4" />
              </Button>
              
              <Button
                onClick={() => navigate("/espace-pro-fastandcar/vehicules")}
                variant="outline"
                size="sm"
                className="border-neon-lime/50 text-neon-lime h-8 w-8 md:h-9 md:w-auto p-0 md:px-3"
                data-testid="vehicles-btn"
              >
                <Car className="w-4 h-4" />
              </Button>
              
              <Button
                onClick={handleLogout}
                variant="outline"
                size="sm"
                className="border-red-800 text-red-500 h-8 w-8 md:h-9 md:w-auto p-0 md:px-3"
              >
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
          
          {/* Mobile search */}
          <div className="sm:hidden mt-2">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
              <Input
                placeholder="Rechercher..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 bg-zinc-900 border-zinc-800 text-white w-full h-9 rounded-none text-sm"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Charts (collapsible) */}
      {showCharts && stats && (
        <section className="py-4 border-b border-zinc-800 bg-zinc-900/50">
          <div className="max-w-7xl mx-auto px-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Revenue */}
              <div className="bg-zinc-900 border border-neon-lime/30 p-4">
                <div className="flex items-center gap-3">
                  <Euro className="w-8 h-8 text-neon-lime" />
                  <div>
                    <p className="text-zinc-500 text-xs">Chiffre d'affaires</p>
                    <p className="font-unbounded text-xl font-bold text-neon-lime">
                      {new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(stats.total_revenue || 0)}
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Temperature Chart */}
              <div className="bg-zinc-900 border border-zinc-800 p-4">
                <ResponsiveContainer width="100%" height={100}>
                  <PieChart>
                    <Pie
                      data={[
                        { name: "Chauds", value: stats.hot, color: LEAD_COLORS.hot },
                        { name: "Tièdes", value: stats.warm, color: LEAD_COLORS.warm },
                        { name: "Froids", value: stats.cold, color: LEAD_COLORS.cold },
                        { name: "Perdus", value: stats.lost, color: LEAD_COLORS.lost },
                      ]}
                      cx="50%"
                      cy="50%"
                      innerRadius={25}
                      outerRadius={40}
                      dataKey="value"
                    >
                      {[LEAD_COLORS.hot, LEAD_COLORS.warm, LEAD_COLORS.cold, LEAD_COLORS.lost].map((color, index) => (
                        <Cell key={`cell-${index}`} fill={color} />
                      ))}
                    </Pie>
                    <Tooltip contentStyle={{ backgroundColor: "#18181b", border: "1px solid #27272a", color: "#fff" }} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              
              {/* Pipeline Chart */}
              <div className="bg-zinc-900 border border-zinc-800 p-4">
                <ResponsiveContainer width="100%" height={100}>
                  <BarChart
                    data={PIPELINE_COLUMNS.slice(0, 6).map(col => ({
                      name: col.label.substring(0, 4),
                      value: stats[`pipeline_${col.key}`] || 0,
                    }))}
                  >
                    <XAxis dataKey="name" tick={{ fill: "#71717a", fontSize: 9 }} />
                    <Bar dataKey="value" fill="#39FF14" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Kanban Board */}
      <section className="py-4 overflow-x-auto">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCorners}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          onDragOver={handleDragOver}
        >
          <div className="flex gap-3 px-4 min-w-max">
            {PIPELINE_COLUMNS.map((column) => {
              const columnLeads = getLeadsByStatus(column.key);
              const Icon = column.icon;
              
              return (
                <div
                  key={column.key}
                  id={column.key}
                  className="w-72 flex-shrink-0 bg-zinc-900/30 border border-zinc-800"
                  data-testid={`column-${column.key}`}
                >
                  {/* Column Header */}
                  <div
                    className="px-3 py-3 border-b-2 flex items-center justify-between"
                    style={{ borderBottomColor: column.color }}
                  >
                    <div className="flex items-center gap-2">
                      <Icon className="w-4 h-4" style={{ color: column.color }} />
                      <span className="font-unbounded text-xs font-bold text-white uppercase tracking-wide">
                        {column.label}
                      </span>
                    </div>
                    <span
                      className="px-2 py-1 text-xs font-bold"
                      style={{ backgroundColor: column.color, color: "#fff" }}
                    >
                      {columnLeads.length}
                    </span>
                  </div>
                  
                  {/* Column Content - Droppable Area */}
                  <DroppableColumn id={column.key}>
                    <SortableContext
                      items={columnLeads.map((l) => l.id)}
                      strategy={verticalListSortingStrategy}
                    >
                      {columnLeads.length === 0 ? (
                        <div 
                          className="text-center py-8 text-zinc-600 text-xs border-2 border-dashed border-zinc-800 rounded"
                        >
                          Glissez un lead ici
                        </div>
                      ) : (
                        columnLeads.map((lead) => (
                          <SortableLeadCard
                            key={lead.id}
                            lead={lead}
                            openLeadDetail={openLeadDetail}
                            formatDate={formatDate}
                          />
                        ))
                      )}
                    </SortableContext>
                  </DroppableColumn>
                </div>
              );
            })}
          </div>
          
          {/* Drag Overlay */}
          <DragOverlay>
            {activeLead ? (
              <LeadCardOverlay lead={activeLead} formatDate={formatDate} />
            ) : null}
          </DragOverlay>
        </DndContext>
      </section>

      {/* Lead Detail Dialog */}
      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
          {selectedLead && (
            <>
              <DialogHeader>
                <DialogTitle className="font-unbounded text-xl flex items-center gap-3">
                  <Car className="w-6 h-6 text-neon-lime" />
                  {selectedLead.name}
                </DialogTitle>
              </DialogHeader>
              
              <div className="grid grid-cols-2 gap-6 mt-4">
                {/* Left Column - Contact & Project */}
                <div className="space-y-4">
                  <div>
                    <p className="text-zinc-500 text-xs mb-1">Contact</p>
                    <div className="space-y-2">
                      <a href={`tel:${selectedLead.phone}`} className="flex items-center gap-2 text-neon-lime hover:underline">
                        <Phone className="w-4 h-4" />
                        {selectedLead.phone}
                      </a>
                      <a href={`mailto:${selectedLead.email}`} className="flex items-center gap-2 text-zinc-400 hover:text-white text-sm">
                        <Mail className="w-4 h-4" />
                        {selectedLead.email}
                      </a>
                    </div>
                  </div>
                  
                  <div>
                    <p className="text-zinc-500 text-xs mb-1">Projet</p>
                    <div className="space-y-1 text-sm">
                      <p><span className="text-zinc-500">Budget:</span> {selectedLead.budget || "-"}</p>
                      <p><span className="text-zinc-500">Véhicule:</span> {selectedLead.vehicle_type || "-"}</p>
                      <p><span className="text-zinc-500">Forfait:</span> {selectedLead.pack_interest || "-"}</p>
                      <p><span className="text-zinc-500">Source:</span> {selectedLead.source}</p>
                      <p><span className="text-zinc-500">Créé le:</span> {formatDateTime(selectedLead.created_at)}</p>
                    </div>
                  </div>
                  
                  {selectedLead.message && (
                    <div>
                      <p className="text-zinc-500 text-xs mb-1">Message</p>
                      <p className="text-sm text-zinc-400 bg-zinc-800/50 p-2">{selectedLead.message}</p>
                    </div>
                  )}
                </div>
                
                {/* Right Column - Status & Actions */}
                <div className="space-y-4">
                  <div>
                    <p className="text-zinc-500 text-xs mb-2">Déplacer vers</p>
                    <div className="grid grid-cols-2 gap-1">
                      {PIPELINE_COLUMNS.map((col) => {
                        const isActive = (selectedLead.pipeline_status || "new") === col.key;
                        const Icon = col.icon;
                        return (
                          <Button
                            key={col.key}
                            onClick={() => {
                              updatePipelineStatus(selectedLead.id, col.key);
                              setSelectedLead({ ...selectedLead, pipeline_status: col.key });
                            }}
                            variant={isActive ? "default" : "outline"}
                            size="sm"
                            className={`text-xs h-8 rounded-none ${!isActive ? "border-zinc-700 text-zinc-400" : ""}`}
                            style={isActive ? { backgroundColor: col.color } : {}}
                          >
                            <Icon className="w-3 h-3 mr-1" />
                            {col.label.substring(0, 8)}
                          </Button>
                        );
                      })}
                    </div>
                  </div>
                  
                  <div>
                    <p className="text-zinc-500 text-xs mb-2">Température</p>
                    <div className="flex gap-1">
                      {Object.entries(LEAD_COLORS).map(([status, color]) => (
                        <Button
                          key={status}
                          onClick={() => {
                            updateLeadStatus(selectedLead.id, status);
                            setSelectedLead({ ...selectedLead, lead_status: status });
                          }}
                          variant={selectedLead.lead_status === status ? "default" : "outline"}
                          size="sm"
                          className={`text-xs h-8 flex-1 rounded-none ${selectedLead.lead_status !== status ? "border-zinc-700" : ""}`}
                          style={selectedLead.lead_status === status ? { backgroundColor: color } : {}}
                        >
                          {status === "hot" ? "🔥" : status === "warm" ? "🌡️" : status === "cold" ? "❄️" : "❌"}
                        </Button>
                      ))}
                    </div>
                  </div>
                  
                  <div>
                    <p className="text-zinc-500 text-xs mb-2">Actions rapides</p>
                    <div className="flex gap-2">
                      <a href={`tel:${selectedLead.phone}`} className="flex-1">
                        <Button className="w-full bg-green-600 text-white rounded-none h-9 text-xs">
                          <Phone className="w-3 h-3 mr-1" /> Appeler
                        </Button>
                      </a>
                      <a href={`https://wa.me/${selectedLead.phone.replace(/\s/g, "").replace(/^0/, "33")}`} target="_blank" rel="noopener noreferrer" className="flex-1">
                        <Button className="w-full bg-[#25D366] text-white rounded-none h-9 text-xs">
                          WhatsApp
                        </Button>
                      </a>
                      <Button
                        onClick={() => setEmailDialogOpen(true)}
                        className="flex-1 bg-blue-600 text-white rounded-none h-9 text-xs"
                      >
                        <Send className="w-3 h-3 mr-1" /> Email
                      </Button>
                    </div>
                  </div>
                  
                  <div>
                    <p className="text-zinc-500 text-xs mb-2 flex items-center gap-1">
                      <MessageSquare className="w-3 h-3" /> Notes ({selectedLead.notes?.length || 0})
                    </p>
                    <div className="space-y-1 max-h-24 overflow-y-auto mb-2">
                      {selectedLead.notes?.map((note, i) => (
                        <p key={i} className="text-xs text-zinc-500 bg-zinc-800/50 p-1">{note}</p>
                      ))}
                    </div>
                    <div className="flex gap-2">
                      <Input
                        placeholder="Ajouter une note..."
                        value={newNote}
                        onChange={(e) => setNewNote(e.target.value)}
                        className="bg-zinc-800 border-zinc-700 text-white h-8 rounded-none text-xs flex-1"
                      />
                      <Button
                        onClick={() => addNote(selectedLead.id)}
                        className="bg-neon-lime text-black h-8 px-3 rounded-none text-xs"
                      >
                        +
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Email Dialog */}
      <Dialog open={emailDialogOpen} onOpenChange={setEmailDialogOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="font-unbounded">
              Envoyer un email
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <Select value={selectedTemplate} onValueChange={setSelectedTemplate}>
              <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white h-10 rounded-none">
                <SelectValue placeholder="Choisir un template" />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-800">
                {EMAIL_TEMPLATES.map((t) => (
                  <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            {selectedTemplate === "custom" && (
              <>
                <Input
                  value={customSubject}
                  onChange={(e) => setCustomSubject(e.target.value)}
                  placeholder="Sujet"
                  className="bg-zinc-800 border-zinc-700 text-white h-10 rounded-none"
                />
                <Textarea
                  value={customMessage}
                  onChange={(e) => setCustomMessage(e.target.value)}
                  placeholder="Message"
                  rows={4}
                  className="bg-zinc-800 border-zinc-700 text-white rounded-none"
                />
              </>
            )}
          </div>
          
          <div className="flex gap-4">
            <Button
              onClick={() => setEmailDialogOpen(false)}
              variant="outline"
              className="flex-1 border-zinc-700 text-white rounded-none"
            >
              Annuler
            </Button>
            <Button
              onClick={sendEmail}
              disabled={!selectedTemplate || sendingEmail}
              className="flex-1 bg-neon-lime text-black font-bold rounded-none"
            >
              {sendingEmail ? "Envoi..." : "Envoyer"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Settings Dialog */}
      <Dialog open={settingsDialogOpen} onOpenChange={setSettingsDialogOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="font-unbounded flex items-center gap-2">
              <Settings className="w-5 h-5 text-neon-lime" />
              Paramètres du compte
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Current Username Display */}
            <div className="bg-zinc-800/50 border border-zinc-700 p-3">
              <p className="text-zinc-500 text-xs mb-1">Compte connecté</p>
              <p className="text-white font-mono">{currentAdminUsername}</p>
            </div>

            {/* New Username */}
            <div className="space-y-2">
              <Label htmlFor="new-username" className="text-white flex items-center gap-2">
                <User className="w-4 h-4 text-neon-lime" />
                Nouvel identifiant
              </Label>
              <Input
                id="new-username"
                type="text"
                value={newUsername}
                onChange={(e) => setNewUsername(e.target.value)}
                placeholder="Nouvel identifiant"
                className="bg-zinc-800 border-zinc-700 text-white h-10 rounded-none"
              />
            </div>

            {/* Current Password (required) */}
            <div className="space-y-2">
              <Label htmlFor="current-password" className="text-white flex items-center gap-2">
                <Key className="w-4 h-4 text-neon-lime" />
                Mot de passe actuel <span className="text-red-500">*</span>
              </Label>
              <div className="relative">
                <Input
                  id="current-password"
                  type={showCurrentPassword ? "text" : "password"}
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  placeholder="Mot de passe actuel"
                  className="bg-zinc-800 border-zinc-700 text-white h-10 rounded-none pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white"
                >
                  {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {/* New Password */}
            <div className="space-y-2">
              <Label htmlFor="new-password" className="text-white flex items-center gap-2">
                <Key className="w-4 h-4 text-zinc-500" />
                Nouveau mot de passe
              </Label>
              <div className="relative">
                <Input
                  id="new-password"
                  type={showNewPassword ? "text" : "password"}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Nouveau mot de passe (min. 6 caractères)"
                  className="bg-zinc-800 border-zinc-700 text-white h-10 rounded-none pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white"
                >
                  {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {/* Confirm New Password */}
            {newPassword && (
              <div className="space-y-2">
                <Label htmlFor="confirm-password" className="text-white flex items-center gap-2">
                  <Key className="w-4 h-4 text-zinc-500" />
                  Confirmer le nouveau mot de passe
                </Label>
                <Input
                  id="confirm-password"
                  type={showNewPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirmer le mot de passe"
                  className={`bg-zinc-800 border-zinc-700 text-white h-10 rounded-none ${
                    confirmPassword && newPassword !== confirmPassword ? "border-red-500" : ""
                  }`}
                />
                {confirmPassword && newPassword !== confirmPassword && (
                  <p className="text-red-500 text-xs">Les mots de passe ne correspondent pas</p>
                )}
              </div>
            )}

            <p className="text-zinc-500 text-xs">
              * Le mot de passe actuel est requis pour toute modification
            </p>
          </div>
          
          <div className="flex gap-4">
            <Button
              onClick={() => {
                setSettingsDialogOpen(false);
                setCurrentPassword("");
                setNewPassword("");
                setConfirmPassword("");
              }}
              variant="outline"
              className="flex-1 border-zinc-700 text-white rounded-none"
            >
              Annuler
            </Button>
            <Button
              onClick={handleSaveSettings}
              disabled={!currentPassword || savingSettings || (newPassword && newPassword !== confirmPassword)}
              className="flex-1 bg-neon-lime text-black font-bold rounded-none"
            >
              {savingSettings ? "Enregistrement..." : "Enregistrer"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminPage;
